import type { To } from 'react-router';
import { merge } from 'lodash-es';
import React, { type ComponentProps, useId } from 'react';

import type { VariantProp } from '../../common/colors';
import type { DirectionTypes } from '../../utilities/shared/Direction';
import type { GutterProp } from '../../utilities/shared/Gutter';
import type { MaxWidthTypes } from '../../utilities/shared/MaxWidth';
import type { MinWidthTypes } from '../../utilities/shared/MinWidth';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { WrapTypes } from '../../utilities/shared/Wrap';
import { Icon } from '../../assets/Icon/Icon';
import { Skeleton } from '../../assets/Skeleton/Skeleton';
import { SPACING_VALUE } from '../../common/sizing';
import { VariantProvider } from '../../common/variant';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { selectors } from '../../controls/shared/styles';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Body, BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { Small, SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { TitleSansSizes } from '../../text/Title';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { directionCSS } from '../../utilities/shared/Direction';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { type WidthTypes, widthCSS } from '../../utilities/shared/Width';
import { wrapCSS } from '../../utilities/shared/Wrap';
import { LabelTooltip } from '../Tooltip/Tooltip';

const statVars = {
  zIndices: {
    dividers: 0,
    mask: 1,
    boundary: 2,
    rest: 3,
  },
};

const DEFAULT_DIVIDER_GAP: SpacingScale = 12;

function findClosestSpacingValue(target: number): SpacingScale {
  return SPACING_VALUE.reduce(
    (closest, current) =>
      Math.abs(current - target) < Math.abs(closest - target) ? current : closest,
    SPACING_VALUE[0] as SpacingScale,
  );
}

function getDividerGapStyle(gap: SpacingScale = DEFAULT_DIVIDER_GAP): React.CSSProperties {
  const position = findClosestSpacingValue(gap / 2);

  return {
    '--dividerPosition': `calc(var(--sizes-${position}) * -1 - 0.5px)`,
  } as React.CSSProperties;
}

const StatDisplay = styled('span', {
  position: 'relative',
  zIndex: statVars.zIndices.rest,
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  height: '100%',
  color: '$$displayColor',

  variants: {
    alignment: {
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
      start: {
        justifyContent: 'flex-start',
      },
    },
    size: {
      small: {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
      large: {
        ...LargeSansSizes,
      },
      'extra-large': {
        ...TitleSansSizes,
        fontWeight: '$bold',
      },
    },
    variant: {
      alternative: {
        color: colors.headingAlternativeLight,

        [darkThemeSelector]: {
          color: colors.headingAlternativeDark,
        },
      },
      attention: {
        color: colors.headingAttentionLight,

        [darkThemeSelector]: {
          color: colors.headingAttentionDark,
        },
      },
      brand: {
        color: colors.headingBrandLight,

        [darkThemeSelector]: {
          color: colors.headingBrandDark,
        },
      },
      negative: {
        color: colors.headingNegativeLight,

        [darkThemeSelector]: {
          color: colors.headingNegativeDark,
        },
      },
      neutral: {
        color: colors.headingNeutralLight,

        [darkThemeSelector]: {
          color: colors.headingNeutralDark,
        },
      },
      positive: {
        color: colors.headingPositiveLight,

        [darkThemeSelector]: {
          color: colors.headingPositiveDark,
        },
      },
    },
  },
});
StatDisplay.displayName = 'StatDisplay';

const StatFootnoteLabel = styled(Small);
StatFootnoteLabel.displayName = 'StatFootnoteLabel';

const StatFootnoteValue = styled(Small);
StatFootnoteValue.displayName = 'StatFootnoteValue';

const StatFootnote = styled('div', {
  position: 'relative',
  zIndex: statVars.zIndices.rest,
  display: 'flex',
  flexDirection: 'row',
  gap: '$4',
});
StatFootnote.displayName = 'StatFootnote';

const StatLabelInner = styled(Text, {
  color: '$$labelColor',
  fontWeight: '$bold',
  whiteSpace: 'nowrap',
});

const StatLabel = styled(Text, {
  display: 'flex',
  width: '100%',

  variants: {
    alignment: {
      center: {
        justifyContent: 'center',
      },
      start: {
        justifyContent: 'flex-start',
      },
      end: {
        justifyContent: 'flex-end',
      },
    },
  },
});
StatLabel.displayName = 'StatLabel';

const StatDivider = styled('div', {
  position: 'absolute',
  zIndex: statVars.zIndices.dividers,
  display: 'flex',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});

const StatDividerEnds = styled(StatDivider, {
  top: 'var(--dividerPosition)',
  right: 0,
  left: 0,
  heightAll: '$1',
});

const StatDividerSides = styled(StatDivider, {
  top: 0,
  right: 'var(--dividerPosition)',
  bottom: 0,
  widthAll: '$1',
});

const StatLegend = styled(Body, {
  display: 'flex',
  flexDirection: 'row',
  color: '$$legendColor',
});
StatLegend.displayName = 'StatLegend';

const StatActions = styled(ControlGroup);
StatActions.displayName = 'StatActions';

const StatHeader = styled(AlignStack, {
  position: 'relative',
  zIndex: statVars.zIndices.rest,
});
StatHeader.displayName = 'StatHeader';

const StatContainer = styled('div', {
  display: 'flex',

  $$legendColor: colors.bodyNeutralLight,
  $$displayColor: colors.headingNeutralLight,

  [darkThemeSelector]: {
    $$legendColor: colors.bodyNeutralDark,
    $$displayColor: colors.headingNeutralDark,
  },

  variants: {
    alignment: {
      center: {
        alignItems: 'center',
      },
      end: {
        alignItems: 'flex-end',
      },
      start: {
        alignItems: 'flex-start',
      },
    },
    appearance: {
      blocks: {
        padding: '$8 $10',
        strokeAll: colors.strokeNeutralLight,
        borderRadius: '$8',

        [darkThemeSelector]: {
          strokeAll: colors.strokeNeutralDark,
        },
      },
      dividers: {
        position: 'relative',
        borderRadius: '$8',

        '&::before': {
          content: '""',
          position: 'absolute',
          zIndex: statVars.zIndices.boundary,
          inset: '-$4',
          borderRadius: '$8',
          pointerEvents: 'none',
        },
      },
      regular: {},
    },
    arrangement: {
      'leading-display': {
        flexDirection: 'column-reverse',
      },
      'leading-label': {
        flexDirection: 'column',
      },
    },
    proportions: {
      equal: {},
      auto: {},
    },
    isPressable: {
      true: {
        borderRadius: '$8',
        cursor: 'pointer',

        [selectors.hover]: {
          backgroundColor: colors.bgNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,
          },

          '&::before': {
            background: colors.bgNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
            },
          },
        },

        [selectors.focus]: {
          outline: 'none',

          '&::before': {
            outline: 'none',
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {},
    },
    selected: {
      true: {
        [selectors.all]: {
          backgroundColor: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$labelColor: colors.bodyBrandLight,
          $$legendColor: colors.bodyBrandLight,
          $$displayColor: colors.headingBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$labelColor: colors.bodyBrandDark,
            $$legendColor: colors.bodyBrandDark,
            $$displayColor: colors.headingBrandDark,
          },
        },
      },
      false: {},
    },
    variant: {
      alternative: {},
      attention: {},
      brand: {},
      negative: {},
      neutral: {},
      positive: {},
    },
    size: {
      small: {
        gap: '$2',
      },
      medium: {
        gap: '$4',
      },
      large: {
        gap: '$4',
      },
      'extra-large': {
        gap: '$4',
      },
    },
    ticker: {
      true: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: '$8',
      },
      false: {
        justifyContent: 'flex-end',
      },
    },
  },

  compoundVariants: [
    {
      selected: true,
      variant: 'alternative',
      css: {
        [selectors.all]: {
          backgroundColor: colors.bgAlternativeLight,
          strokeAll: colors.strokeAlternativeLight,
          $$labelColor: colors.bodyAlternativeLight,
          $$legendColor: colors.bodyAlternativeLight,
          $$displayColor: colors.headingAlternativeLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgAlternativeDark,
            strokeAll: colors.strokeAlternativeDark,
            $$labelColor: colors.bodyAlternativeDark,
            $$legendColor: colors.bodyAlternativeDark,
            $$displayColor: colors.headingAlternativeDark,
          },
        },
      },
    },
    {
      selected: true,
      variant: 'attention',
      css: {
        [selectors.all]: {
          backgroundColor: colors.bgAttentionLight,
          strokeAll: colors.strokeAttentionLight,
          $$labelColor: colors.bodyAttentionLight,
          $$legendColor: colors.bodyAttentionLight,
          $$displayColor: colors.headingAttentionLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgAttentionDark,
            strokeAll: colors.strokeAttentionDark,
            $$labelColor: colors.bodyAttentionDark,
            $$legendColor: colors.bodyAttentionDark,
            $$displayColor: colors.headingAttentionDark,
          },
        },
      },
    },
    {
      selected: true,
      variant: 'negative',
      css: {
        [selectors.all]: {
          backgroundColor: colors.bgNegativeLight,
          strokeAll: colors.strokeNegativeLight,
          $$labelColor: colors.bodyNegativeLight,
          $$legendColor: colors.bodyNegativeLight,
          $$displayColor: colors.headingNegativeLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNegativeDark,
            strokeAll: colors.strokeNegativeDark,
            $$labelColor: colors.bodyNegativeDark,
            $$legendColor: colors.bodyNegativeDark,
            $$displayColor: colors.headingNegativeDark,
          },
        },
      },
    },
    {
      selected: true,
      variant: 'neutral',
      css: {
        [selectors.all]: {
          backgroundColor: colors.bgNeutralLight,
          strokeAll: colors.strokeNeutralLight,
          $$labelColor: colors.bodyNeutralLight,
          $$legendColor: colors.bodyNeutralLight,
          $$displayColor: colors.headingNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,
            strokeAll: colors.strokeNeutralDark,
            $$labelColor: colors.bodyNeutralDark,
            $$legendColor: colors.bodyNeutralDark,
            $$displayColor: colors.headingNeutralDark,
          },
        },
      },
    },
    {
      selected: true,
      variant: 'positive',
      css: {
        [selectors.all]: {
          backgroundColor: colors.bgPositiveLight,
          strokeAll: colors.strokePositiveLight,
          $$labelColor: colors.bodyPositiveLight,
          $$legendColor: colors.bodyPositiveLight,
          $$displayColor: colors.headingPositiveLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgPositiveDark,
            strokeAll: colors.strokePositiveDark,
            $$labelColor: colors.bodyPositiveDark,
            $$legendColor: colors.bodyPositiveDark,
            $$displayColor: colors.headingPositiveDark,
          },
        },
      },
    },
    {
      proportions: 'equal',
      ticker: false,
      css: {
        flex: '1 1 0',
      },
    },
    {
      arrangement: 'leading-display',
      ticker: false,
      css: {
        flexDirection: 'column-reverse',
      },
    },
    {
      arrangement: 'leading-label',
      ticker: false,
      css: {
        flexDirection: 'column',
      },
    },
  ],
});
StatContainer.displayName = 'StatContainer';

type StatAlignmentProp = 'center' | 'end' | 'start';
type StatAppearanceProp = 'blocks' | 'dividers' | 'regular';
type StatArrangementProp = 'leading-display' | 'leading-label';
type StatProportionsProp = 'equal' | 'auto';
type StatSizeProp = 'small' | 'medium' | 'large' | 'extra-large';

// Skeleton dimensions matching text line-heights
const statSkeletonSize: Record<StatSizeProp, { height: string; width: string }> = {
  small: { height: '20px', width: '48px' },
  medium: { height: '24px', width: '56px' },
  large: { height: '24px', width: '56px' },
  'extra-large': { height: '32px', width: '72px' },
};

export type StatProps = {
  /**
   * The actions to display to the right of the stat.
   */
  actions?: React.ReactNode;
  /**
   * The alignment of the stat.
   */
  alignment?: StatAlignmentProp;
  /**
   * The appearance of the stat.
   */
  appearance?: StatAppearanceProp;
  /**
   * The arrangement of the stat.
   */
  arrangement?: StatArrangementProp;
  /**
   * A footnote to display below the stat.
   */
  footnote?: {
    label?: React.ReactNode;
    value: React.ReactNode;
    variant?: VariantProp;
  };
  /**
   * The gap spacing for divider positioning when appearance is dividers.
   */
  gap?: SpacingScale;
  /**
   * The label to display above the stat. Can be a string, a React node, or an object with children and variant props.
   */
  label: React.ReactNode | { copy: React.ReactNode; variant?: VariantProp };
  /**
   * The legend to display to the right of the label.
   */
  legend?: React.ReactNode;
  /**
   * Whether the stat is loading, shows a loading state when true.
   */
  isLoading?: boolean;
  /**
   * The proportions of the stat.
   */
  proportions?: StatProportionsProp;
  /**
   * Whether the stat is selected.
   */
  selected?: boolean;
  /**
   * The size of the stat.
   */
  size?: StatSizeProp;
  /**
   * Whether the stat is a ticker.
   */
  ticker?: boolean;
  /**
   * A tooltip to display when hovering over the stat.
   */
  tooltip?: React.ReactNode;
  /**
   * The value to display in the stat.
   */
  value: React.ReactNode;
  /**
   * A variant to apply to the stat.
   */
  variant?: VariantProp;
  /**
   * Multiple width props for the stat.
   */
  width?: {
    width?: WidthTypes;
    maxWidth?: MaxWidthTypes;
    minWidth?: MinWidthTypes;
  };
  /**
   * A callback to call when the stat is clicked.
   */
  onClick?: () => void;
  // Routing
  as?: React.ElementType;
  to?: To;
  /**
   * Whether to show the pressable state.
   */
  showPressable?: boolean;
  /**
   * Indicates whether this Stat is the last one in the list, used to dynamically render dividers
   */
  isLast?: boolean;
} & Omit<ComponentProps<typeof StatContainer>, 'gap'>;

function Stat({
  actions,
  alignment,
  appearance = 'regular',
  arrangement,
  footnote,
  gap,
  label,
  legend,
  isLoading,
  onClick,
  selected,
  size,
  ticker,
  tooltip,
  value,
  variant,
  width,
  as,
  to,
  showPressable,
  isLast,
  ...remaining
}: StatProps) {
  const labelVariant =
    label && typeof label === 'object' && 'variant' in label ? label.variant : variant;
  const isPressableWindow = as && to;
  const isPressable = Boolean(onClick || isPressableWindow);
  const hasHeaderEnd = Boolean(legend || actions || isPressable);
  const widthProperty = width && width.width ? widthCSS(width.width) : undefined;
  const maxWidthProperty = width && width.maxWidth ? maxWidthCSS(width.maxWidth) : undefined;
  const minWidthProperty = width && width.minWidth ? minWidthCSS(width.minWidth) : undefined;

  const css = merge(widthProperty, maxWidthProperty, minWidthProperty);

  const gapStyle = appearance === 'dividers' ? getDividerGapStyle(gap) : undefined;

  return (
    <StatContainer
      as={as}
      to={to}
      css={css}
      style={gapStyle}
      alignment={alignment}
      appearance={appearance}
      arrangement={arrangement}
      selected={selected}
      size={size}
      ticker={ticker}
      variant={variant}
      tabIndex={isPressable ? 0 : undefined}
      isPressable={isPressable}
      onClick={onClick}
      role={isPressable ? 'button' : undefined}
      onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
        if (isPressable && e.code === 'Enter') {
          onClick?.();
        }
      }}
      {...remaining}
    >
      <StatHeader
        gap={4}
        preset={size === 'large' ? 'body' : 'small'}
        width={!ticker ? '100%' : undefined}
        end={
          hasHeaderEnd && (
            <>
              {legend && <StatLegend>{legend}</StatLegend>}
              {actions && (
                <StatActions relation="separate" size="small">
                  {actions}
                </StatActions>
              )}
              {isPressableWindow && showPressable && (
                <Icon icon="arrow-up-right" size={10} variant="neutral" />
              )}
            </>
          )
        }
      >
        {label && (
          <StatLabel alignment={alignment}>
            <LabelTooltip
              contents={tooltip}
              preset={size === 'large' ? 'body' : 'small'}
              variant={labelVariant}
            >
              <StatLabelInner>
                {typeof label === 'object' && 'copy' in label ? label.copy : label}
              </StatLabelInner>
            </LabelTooltip>
          </StatLabel>
        )}
      </StatHeader>
      <StatDisplay alignment={alignment} size={size} variant={variant}>
        {isLoading ? (
          <span style={{ display: 'inline-block', marginTop: 4 }}>
            <Skeleton
              height={
                statSkeletonSize[(typeof size === 'string' ? size : 'large') as StatSizeProp].height
              }
              width={
                statSkeletonSize[(typeof size === 'string' ? size : 'large') as StatSizeProp].width
              }
              radius="4px"
            />
          </span>
        ) : (
          <VariantProvider value={variant}>{value}</VariantProvider>
        )}
      </StatDisplay>
      {footnote && (
        <StatFootnote>
          {footnote.label != null && <StatFootnoteLabel>{footnote.label}</StatFootnoteLabel>}
          <StatFootnoteValue context="body" variant={footnote.variant}>
            {footnote.value}
          </StatFootnoteValue>
        </StatFootnote>
      )}
      {appearance === 'dividers' && (
        <>
          <StatDividerEnds />
          {!isLast && <StatDividerSides />}
        </>
      )}
    </StatContainer>
  );
}

const StatsSeparator = styled('div', {
  display: 'flex',
  width: '$2',
  height: '$16',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});
StatsSeparator.displayName = 'StatsSeparator';

const StatsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  position: 'relative',
  width: '100%',

  variants: {
    appearance: {
      blocks: {},
      dividers: {
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          zIndex: statVars.zIndices.mask,
          display: 'flex',
          backgroundColor: colors.bgApplicationLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgApplicationDark,
          },
        },

        '&::before': {
          top: 'var(--dividerPosition)',
          right: 0,
          left: 0,
          height: '$2',
        },

        '&::after': {
          top: 0,
          right: 'var(--dividerPosition)',
          bottom: 0,
          width: '$2',
        },
      },
      regular: {},
    },
    height: {
      auto: {},
      fill: {
        height: '100%',
      },
    },
    proportions: {
      auto: {},
      equal: {},
    },
    size: {
      small: {},
      medium: {},
      large: {},
      'extra-large': {},
    },
    ticker: {
      true: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        overflow: 'auto',
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      appearance: 'blocks',
      size: 'small',
      css: {
        gap: '$8',
      },
    },
    {
      appearance: 'regular',
      size: 'small',
      css: {
        columnGap: '$4',
        rowGap: '$12',
      },
    },
    {
      appearance: 'blocks',
      size: 'medium',
      css: {
        gap: '$12',
      },
    },
    {
      appearance: 'regular',
      size: 'medium',
      css: {
        columnGap: '$8',
        rowGap: '$16',
      },
    },
    {
      appearance: 'blocks',
      size: 'large',
      css: {
        gap: '$16',
      },
    },
    {
      appearance: 'regular',
      size: 'large',
      css: {
        columnGap: '$12',
        rowGap: '$20',
      },
    },
    {
      appearance: 'blocks',
      size: 'extra-large',
      css: {
        gap: '$20',
      },
    },
    {
      appearance: 'regular',
      size: 'extra-large',
      css: {
        columnGap: '$20',
        rowGap: '$32',
      },
    },
    {
      ticker: true,
      css: {
        gap: '$16',
      },
    },
  ],
});
StatsContainer.displayName = 'StatsContainer';

const StatsWrapper = styled('div', {
  display: 'flex',
  width: '100%',

  variants: {
    height: {
      auto: {},
      fill: {
        height: '100%',
      },
    },
  },
});
StatsWrapper.displayName = 'StatsWrapper';

export type StatsProps = {
  alignment?: StatAlignmentProp;
  appearance?: StatAppearanceProp;
  arrangement?: StatArrangementProp;
  direction?: DirectionTypes;
  gap?: SpacingScale;
  height?: 'auto' | 'fill';
  proportions?: StatProportionsProp;
  selected?: boolean;
  spacing?: SpacingScale;
  size?: StatSizeProp;
  stats: StatProps[];
  ticker?: boolean;
  showPressable?: boolean;
  wrap?: WrapTypes;
  width?: {
    width?: WidthTypes;
    maxWidth?: MaxWidthTypes;
    minWidth?: MinWidthTypes;
  };
} & GutterProp &
  ComponentProps<typeof StatsContainer>;

function getKey(id: string, stat: StatProps) {
  if (stat.key) {
    return stat.key;
  }
  return `${id}-${stat.label}`;
}

export function Stats({
  alignment = 'start',
  appearance = 'regular',
  arrangement = 'leading-label',
  direction = 'row',
  gap,
  gutter = { edge: 'all' },
  height = 'auto',
  proportions = 'equal',
  selected,
  size = 'large',
  stats,
  ticker = false,
  wrap = 'wrap',
  width,
  key,
  showPressable = true,
  ...remaining
}: StatsProps) {
  const id = useId();
  const effectiveGap = appearance === 'dividers' && gap == null ? DEFAULT_DIVIDER_GAP : gap;
  const dividerGapStyle = appearance === 'dividers' ? getDividerGapStyle(effectiveGap) : undefined;

  return (
    <StatsWrapper
      height={height}
      css={gutterCSS({ context: size === 'small' ? 'content' : 'application', edge: gutter.edge })}
      {...remaining}
    >
      <StatsContainer
        appearance={appearance}
        height={height}
        proportions={proportions}
        size={size}
        ticker={ticker}
        css={merge(directionCSS(direction), spacingCSS(effectiveGap), wrapCSS(wrap))}
        style={dividerGapStyle}
      >
        {stats.map((stat, index) => (
          <React.Fragment key={getKey(id, stat)}>
            <Stat
              alignment={alignment}
              appearance={appearance}
              arrangement={arrangement}
              gap={effectiveGap}
              proportions={proportions}
              selected={selected}
              size={size}
              ticker={ticker}
              showPressable={showPressable}
              width={width}
              {...stat}
              isLast={index === stats.length - 1}
            />
            {ticker && index !== stats.length - 1 && <StatsSeparator />}
          </React.Fragment>
        ))}
      </StatsContainer>
    </StatsWrapper>
  );
}
